import { useTranslation } from "react-i18next";

import { useFramework, frameworkLabel } from "../framework";
import type { ReportAggregates } from "./reportTypes";
import { StateBar } from "./components/StateBar";
import { pct } from "./format";

/**
 * Theory of Change tab (Sue's wishlist #4).
 *
 * Promotes the Theory of Change outcomes — which otherwise sit quietly inside
 * the Population/Te Pou views — into their own tab, grouped by domain (the
 * documented ToC grouping; ADR-005). Each outcome is shown prominently with the
 * entry→exit movement for its subdomain, so "are we moving the needle on this
 * outcome?" reads at a glance. De-identified aggregates only.
 */
type Props = { agg: ReportAggregates };

export function ToCView({ agg }: Props) {
  const { t, i18n } = useTranslation();
  const { domains, subdomainsForDomain } = useFramework();
  const subAgg = (id: string) =>
    agg.bySubdomain.find((s) => s.subdomainId === id);

  return (
    <div className="rpt-view">
      <section className="rpt-panel">
        <h2 className="rpt-panel__title">{t("reports.toc.title")}</h2>
        <p className="rpt-panel__lead">{t("reports.toc.lead")}</p>
      </section>

      {domains.map((domain) => {
        const subs = subdomainsForDomain(domain.id);
        if (subs.length === 0) return null;
        return (
          <section key={domain.id} className="rpt-panel">
            <h3 className="rpt-subhead">
              {frameworkLabel(domain, "domain", i18n.language, t)}
            </h3>
            <ul
              className="rpt-toc__list"
              style={{ listStyle: "none", margin: 0, padding: 0 }}
            >
              {subs.map((s) => {
                const a = subAgg(s.id);
                const hasData = a !== undefined && a.analysis.total > 0;
                return (
                  <li
                    key={s.id}
                    className="rpt-toc__item"
                    style={{
                      borderLeft: "4px solid var(--teal, #2a9d8f)",
                      padding: "10px 14px",
                      margin: "10px 0",
                      background: "var(--surface, #fff)",
                      borderRadius: "8px",
                    }}
                  >
                    <p
                      className="rpt-toc__outcome"
                      style={{
                        margin: 0,
                        fontSize: "1.05rem",
                        fontWeight: 600,
                        color: "var(--navy, #1d3557)",
                      }}
                    >
                      {s.toCOutcome || "—"}
                    </p>
                    <p
                      className="rpt-toc__sub"
                      style={{
                        margin: "2px 0 8px",
                        fontSize: "0.85rem",
                        color: "var(--ink-muted, #5a6472)",
                      }}
                    >
                      {frameworkLabel(s, "subdomain", i18n.language, t)}
                    </p>
                    {hasData ? (
                      <div className="rpt-toc__movement">
                        <StateBar
                          proportion={a.exitProportion}
                          phaseLabel={t("reports.atExit")}
                        />
                        <p
                          className="rpt-toc__improved"
                          style={{
                            margin: "6px 0 0",
                            fontSize: "0.85rem",
                            color: "var(--state-protective, #2e7d52)",
                          }}
                        >
                          {t("reports.toc.improved", {
                            pct: pct(a.analysis.improved / a.analysis.total),
                            n: a.analysis.total,
                          })}
                        </p>
                      </div>
                    ) : (
                      <p
                        className="rpt-toc__empty"
                        style={{
                          margin: 0,
                          fontSize: "0.85rem",
                          color: "var(--ink-muted, #9aa3af)",
                        }}
                      >
                        {t("reports.toc.noData")}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

export default ToCView;
